import { useState, type ReactNode } from "react";
import { TurningMark } from "../../../components/brand";
import { DomainFilter } from "../../../components/DomainFilter";
import { URLS } from "../../../constans";
import { AuthGridTiles } from "./AuthGridTiles";

/** Width of a hairline, in CSS px. On a 2x display this is one crisp device pixel. */
const HAIRLINE = "0.5px";

type Props = {
  children: ReactNode;
  /** Hide the brand mark above the content (e.g. while a full-card loader shows). */
  hideMark?: boolean;
  /** Pinned to the page's top-right corner (e.g. the portal language menu). */
  topRight?: ReactNode;
  /** Show the Service Agreement consent line under the card. */
  legal?: boolean;
};

/**
 * The brand mark, turning once on load and once per hover. A turn always runs
 * to the end; hovering mid-turn does nothing.
 */
function AuthMark() {
  const [turning, setTurning] = useState(true);
  return (
    <TurningMark
      // A multiple of 3 keeps the rows on whole pixels.
      size={36}
      turning={turning}
      onTurnEnd={() => setTurning(false)}
      onPointerEnter={() => setTurning(true)}
      role="img"
      aria-label="CaseBlink"
      className="text-brand-blue"
    />
  );
}

/**
 * Signed-out page chrome shared by sign-in, password reset, email action links
 * and the portals: a warm paper page, four hairlines framing an opaque white
 * card, and drifting tiles on the card's grid.
 */
export function AuthShell({
  children,
  hideMark = false,
  topRight,
  legal = true
}: Props) {
  // Sized against a 14px root; the spacing unit keeps those measurements.
  return (
    <div className="bg-paper relative flex min-h-dvh flex-col overflow-hidden [--spacing:0.21875rem]">
      {topRight ? (
        <div className="absolute top-3 right-3 z-40 sm:top-4 sm:right-4">
          {topRight}
        </div>
      ) : null}

      <div className="relative z-10 flex flex-1 items-stretch justify-center sm:items-center sm:py-20">
        {/* Card box. Tiles and frame lines are positioned against this element,
            so keep its width in step with `CARD_WIDTH` in AuthGridTiles. */}
        <div className="relative isolate flex w-full flex-col sm:block sm:w-[480px]">
          <AuthGridTiles />

          {/* Four hairlines run along the card's edges out to the viewport. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-20 hidden sm:block"
          >
            <span
              className="bg-hairline absolute bottom-full left-1/2 w-[200vw] -translate-x-1/2"
              style={{ height: HAIRLINE }}
            />
            <span
              className="bg-hairline absolute top-full left-1/2 w-[200vw] -translate-x-1/2"
              style={{ height: HAIRLINE }}
            />
            <span
              className="bg-hairline absolute top-1/2 right-full h-[200vh] -translate-y-1/2"
              style={{ width: HAIRLINE }}
            />
            <span
              className="bg-hairline absolute top-1/2 left-full h-[200vh] -translate-y-1/2"
              style={{ width: HAIRLINE }}
            />
          </div>

          <main
            id="main"
            className="relative z-30 flex w-full flex-1 flex-col justify-center bg-white px-6 py-12 sm:flex-none sm:px-12 sm:pt-12 sm:pb-28"
          >
            <div className="flex flex-col items-start gap-9">
              {!hideMark && <AuthMark />}
              <div className="flex w-full flex-col">{children}</div>
            </div>
          </main>
        </div>
      </div>

      {legal ? (
        <footer className="relative z-20 px-6 pt-6 pb-10 text-center text-sm leading-4.5 text-ink-quiet">
          By continuing, you agree to{" "}
          <DomainFilter inline>CaseBlink&apos;s</DomainFilter>{" "}
          <a
            href={URLS.serviceAgreement}
            target="_blank"
            rel="noreferrer"
            className="rounded-control underline underline-offset-2 hover:text-ink focus-visible:outline-offset-4"
          >
            Service Agreement
          </a>{" "}
          and{" "}
          <a
            href={URLS.privacy}
            target="_blank"
            rel="noreferrer"
            className="rounded-control underline underline-offset-2 hover:text-ink focus-visible:outline-offset-4"
          >
            Privacy Policy
          </a>
          .
        </footer>
      ) : (
        <div className="pb-10" />
      )}
    </div>
  );
}
